import { SsoConfig } from 'lib/auth';
import { nanoid } from 'nanoid';
import { bufferToBase64UrlEncoded, sha256 } from 'utils/crypto';

export function isTenantDomain() {
  const tenant = getTenant();
  return tenant != null;
}

export function getTenant() {
  const canonicalDomains = process.env.REACT_APP_CANONICAL_DOMAINS?.split(',');
  if (!canonicalDomains?.length) {
    return null;
  }
  const { hostname } = window.location;
  if (canonicalDomains.indexOf(hostname) > -1) {
    return null;
  }
  const subdomain = hostname.match(/^[^.]+/);
  return subdomain?.[0] ?? null;
}

export function getSsoConfig(ssoConfig: string | null) {
  if (ssoConfig) {
    return ssoConfig;
  }
  const tenant = getTenant();
  if (!tenant) {
    throw new Error('Unable to determine sso config name');
  }
  return tenant;
}

export const supportedGrantTypes = ['implicit', 'adfs-implicit', 'auth_code'];

export function validateSsoType(provider: SsoConfig) {
  if (
    provider.type !== 'oidc' ||
    supportedGrantTypes.indexOf(provider.grantType) === -1
  ) {
    throw new Error(
      'We only support OpenID Connect Implicit flow and AuthCode flow for SSO',
    );
  }
}

export const createOicdImplicitFlowAuthUrl = (
  config: SsoConfig,
  email?: string,
  onTenantDomain?: boolean,
) => {
  const nonce = generateSecurityToken(config.name, 'nonce');
  const state = generateSecurityToken(config.name, 'state');
  const isAdfs = config.grantType === 'adfs-implicit';
  const { location } = window;
  const redirectUri = `${location.origin}/login/${
    onTenantDomain ? '' : config.name + '/'
  }callback`;
  const params: { [param: string]: string } = {
    client_id: config.clientId,
    // for adfs we need access token too
    response_type: 'id_token' + (isAdfs ? ' token' : ''),
    scope: ['openid'].concat(config.additionalScopes || '').join(' '),
    redirect_uri: redirectUri,
    nonce,
    state,
  };
  if (email) {
    params['login_hint'] = email;
  }
  const urlParams = new URLSearchParams(params);
  return `${config.authEndpoint}?${urlParams.toString()}`;
};

const generateSecurityToken = (
  ssoConfigName: string,
  key: string,
  size?: number,
) => {
  const value = nanoid(size);
  sessionStorage.setItem(`sso.${ssoConfigName}.${key}`, value);
  return value;
};

export const createOicdAuthCodeFlowAuthUrl = async (
  config: SsoConfig,
  email?: string,
) => {
  const nonce = generateSecurityToken(config.name, 'nonce', 64);
  const state = generateSecurityToken(config.name, 'state', 64);
  const code_verifier = generateSecurityToken(config.name, 'code_verifier', 64);
  const { location } = window;
  const params: { [param: string]: string } = {
    client_id: config.clientId,
    response_type: 'code',
    redirect_uri: `${location.origin}/login/callback`,
    scope: ['openid'].concat(config.additionalScopes || '').join(' '),
    response_mode: 'fragment',
    state: state,
    nonce,
    code_challenge: await getCodeChallenge(code_verifier),
    code_challenge_method: 'S256',
  };
  if (email) {
    params['login_hint'] = email;
  }
  sessionStorage.setItem('sso.ConfigName', config.name);
  sessionStorage.setItem(`sso.${config.name}.client_id`, config.clientId);
  sessionStorage.setItem(
    `sso.${config.name}.tokenEndpoint`,
    config.tokenEndpoint ?? '',
  );
  const urlSearchParams = new URLSearchParams(params);
  return `${config.authEndpoint}?${urlSearchParams.toString()}`;
};

const getCodeChallenge = async (code_verifier: string) => {
  return sha256(code_verifier).then(bufferToBase64UrlEncoded);
};
